import React, { createContext, useContext, useState, useEffect } from 'react';
import { configuracionApi } from '../services/api';
import { Configuracion } from '../objetos/objeto_configuracion';

// Crear el contexto
const ConfiguracionContext = createContext();

// Hook personalizado para usar el contexto
export const useConfiguracion = () => {
    const context = useContext(ConfiguracionContext);
    if (!context) {
        throw new Error('useConfiguracion debe ser usado dentro de un ConfiguracionProvider');
    }
    return context;
};

// Provider del contexto
export const ConfiguracionProvider = ({ children }) => {
    const [configuracion, setConfiguracion] = useState(null);
    const [loadingConfiguracion, setLoadingConfiguracion] = useState(true);
    const [errorConfiguracion, setErrorConfiguracion] = useState(null);
    const [fromCache, setFromCache] = useState(false); // NUEVO: Flag para saber si viene de cache

    // Configuración del cache
    const CACHE_KEY = 'dufy_configuracion_cache';
    const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos en millisegundos

    // Función para cargar desde cache
    const cargarDesdeCache = () => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return false;

            const { data, timestamp } = JSON.parse(cached);
            const now = Date.now();

            if (now - timestamp < CACHE_DURATION) {
                
                const configuracionObj = Configuracion.fromJson(data);
                setConfiguracion(configuracionObj);
                setFromCache(true);
                setLoadingConfiguracion(false);
                
                return true;
            } else {
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
        } catch (error) {
            localStorage.removeItem(CACHE_KEY); // Limpiar cache corrupto
            return false;
        }
    };

    // Función para guardar en cache
    const guardarEnCache = (data) => {
        try {
            const cacheData = {
                data: data,
                timestamp: Date.now()
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error("❌ Error guardando en cache:", error);
        }
    };

    // Función para cargar desde API
    const cargarConfiguracionAPI = async (fromBackground = false) => {
        
        try {
            if (!fromBackground) {
                setLoadingConfiguracion(true);
            }
            setErrorConfiguracion(null);
            
            const data = await configuracionApi.getConfiguracion();
            
            const configuracionObj = Configuracion.fromJson(data);
            
            // Guardar en cache
            guardarEnCache(data);
            
            // Actualizar estado
            setConfiguracion(configuracionObj);
            setFromCache(false);
            
            
            return configuracionObj;
        } catch (error) {
            
            if (!fromBackground) {
                const errorMessage = error.message || "Error al cargar la configuración";
                setErrorConfiguracion(errorMessage);
            }
            
            return null;
        } finally {
            if (!fromBackground) {
                setLoadingConfiguracion(false);
            }
        }
    };

    // Función principal de carga
    const cargarConfiguracionDufy = async () => {
        
        // Paso 1: Intentar cargar desde cache
        const cacheLoaded = cargarDesdeCache();
        
        if (cacheLoaded) {
            // Cache cargado exitosamente, cargar API en background
            
            // Actualizar en background sin afectar la UI
            setTimeout(() => {
                cargarConfiguracionAPI(true).catch(error => {
                });
            }, 100); // Pequeño delay para no bloquear
            
        } else {
            // No hay cache válido, cargar desde API normalmente
            await cargarConfiguracionAPI(false);
        }
    };

    // Función para forzar recarga (sin cache)
    const recargarConfiguracion = async () => {
        console.log("🔄 Forzando recarga de configuración...");
        localStorage.removeItem(CACHE_KEY); // Limpiar cache
        await cargarConfiguracionAPI(false);
    };

    // Función para limpiar cache manualmente
    const limpiarCache = () => {
        localStorage.removeItem(CACHE_KEY);
        setFromCache(false);
        console.log("🗑️ Cache de configuración limpiado");
    };

    const actualizarConfiguracion = (nuevaConfiguracion) => {
        setLoadingConfiguracion(true); 
        setConfiguracion(nuevaConfiguracion);
        
        if (nuevaConfiguracion) {
            guardarEnCache(nuevaConfiguracion);
        }

        setLoadingConfiguracion(false); 
    
    };

    const limpiarError = () => {
        setErrorConfiguracion(null);
    };

    // Cargar configuración al montar el componente
    useEffect(() => {
        cargarConfiguracionDufy();
        
        // Cleanup: No hay sockets que limpiar en este caso
        return () => {
            console.log("🔧 ConfiguracionProvider desmontado");
        };
    }, []);

    const value = {
        // Estados
        configuracion,
        loadingConfiguracion,
        errorConfiguracion,
        fromCache, // NUEVO: Para saber si viene de cache
        
        // Métodos
        cargarConfiguracionDufy,
        recargarConfiguracion, // NUEVO: Forzar recarga
        limpiarCache, // NUEVO: Limpiar cache
        actualizarConfiguracion,
        limpiarError,
    };

    return (
        <ConfiguracionContext.Provider value={value}>
            {children}
        </ConfiguracionContext.Provider>
    );
};